<template>
  <div ref="target" class="animated-container">
    <slot />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import debounce from 'lodash.debounce';

const props = defineProps({
  topOffset: {
    type: Number,
    default: 0,
  },
  threshold: {
    type: Number,
    default: 0.25,
  },
});

const target = ref(null);
const container = {};

const onResize = debounce(() => {
  setContainerDimensions();
  calculateContainerSize();
}, 50);

onMounted(() => {
  setContainerDimensions();
  calculateContainerSize();

  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('scroll', calculateContainerSize, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
  window.removeEventListener('scroll', calculateContainerSize);
});

function setContainerDimensions() {
  container.layout = target.value.querySelector('.section-layout');
  container.layout.style.maxWidth = null;
  container.layout.style.height = null;
  container.layout.style.marginRight = null;
  container.layout.style.marginLeft = null;
  const layoutStyles = window.getComputedStyle(container.layout);
  container.initialOffsetTop = target.value.offsetTop;
  container.initialWidth = parseFloat(layoutStyles.width);
  container.initialHeight = parseFloat(layoutStyles.height);
  container.initialMargin = parseFloat(layoutStyles.marginRight);
}

function calculateContainerSize() {
  const scrollOffset = window.scrollY;
  const minScrollOffset = props.topOffset;
  const maxScrollOffset = Math.ceil(container.initialOffsetTop + (container.initialHeight * props.threshold));

  const minWidth = container.initialWidth;
  const maxWidth = window.innerWidth;
  const scaledWidth = scaleBetween(scrollOffset, [minWidth, maxWidth], [minScrollOffset, maxScrollOffset]);
  const newWidth = Math.min(scaledWidth, maxWidth);
  container.layout.style.maxWidth = `${newWidth}px`;

  const minMargin = container.initialMargin;
  const maxMargin = 0;
  const scaledMargin = scaleBetween(scrollOffset, [minMargin, maxMargin], [minScrollOffset, maxScrollOffset]);
  const newMargin = Math.max(scaledMargin, maxMargin);
  container.layout.style.marginRight = `${newMargin}px`;
  container.layout.style.marginLeft = `${newMargin}px`;
}

function scaleBetween(unscaledNum, [minAllowed, maxAllowed], [min, max]) {
  return ((maxAllowed - minAllowed) * (unscaledNum - min)) / (max - min) + minAllowed;
}
</script>
